// reflow — детерминированная пересборка финалов live-распознавания в абзацы.
// Границы «финалов» ставит акустика (VAD-пауза 0.8с / принудительный разрез 22с),
// поэтому фразы рвутся посреди предложения. Здесь склеиваем обратно по пунктуации
// GigaAM и режем на абзацы по паузам между высказываниями. Чистые функции, без
// состояния — общие для live-вью (LiveTranscript) и экспорта (StreamingTranscription).
// Спека: docs/superpowers/specs/2026-07-12-streaming-reflow-design.md

// Пауза после ЗАКОНЧЕННОГО предложения, открывающая новый абзац (сек).
export const PARA_GAP_SEC = 1.2;
// Пауза, рвущая абзац даже без знака конца предложения (страховка, сек).
export const HARD_GAP_SEC = 6.0;

// Конец предложения: . ! ? …, допуская закрывающие кавычки/скобки после знака («.»», «?)»).
const SENTENCE_END_RE = /[.!?…]["»)\]]*$/;

// Дедуп стыка форс-сплита: при монологе > max_utt_sec бэкенд рубит высказывание
// и сеет следующее пре-роллом (хвост ~0.5с предыдущего) — этот кусок аудио
// декодируется ДВАЖДЫ, на стыке дублируются слова («…поможет, мы Поможет, мы…»).
// Артефактный стык отличаем по ВРЕМЕНИ: сегменты перекрываются (gap < порога),
// тогда как реальная VAD-пауза даёт gap ≥ ~0.3с — осознанные повторы не трогаем.
export const DEDUP_GAP_MAX_SEC = 0.25;
const DEDUP_MAX_WORDS = 4; // хвост 0.5с — это 1–3 слова

const normWord = (w) => w.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

// Снять капс начала высказывания при вклейке next в середину предложения
// («Перевернула их…» → «перевернула их…»); слова целиком в верхнем регистре
// (США) и уже строчные не трогаем.
const decap = (text) => (/^[А-ЯЁ][а-яё]/.test(text) ? text[0].toLowerCase() + text.slice(1) : text);

// Склейка стыка с обрезкой дубля. Точный повтор k слов (допуская одно лишнее
// слово в начале next) режется из next. Краевое слово prev декодировано из
// ОБРЕЗАННОГО форс-сплитом аудио и потому ненадёжно: если оно оборвано на
// полуслове («…и Бел.» + «и Беларусь») или разошлось с декодом next при точном
// якоре рядом («…перевернула её» + «Перевернула их…»), режем хвост prev и
// оставляем next целиком — его декод видел этот кусок с полным контекстом.
// Нет пересечения → обычная склейка через пробел.
export function mergeJunction(prev, next) {
  const pw = prev.split(/\s+/);
  const nw = next.split(/\s+/);
  for (let s = 0; s <= 1; s++) {
    const maxK = Math.min(DEDUP_MAX_WORDS, pw.length, nw.length - s);
    for (let k = maxK; k >= 1; k--) {
      let trustNext = false; // краевое слово prev ненадёжно → prev теряет хвост
      let ok = true;
      for (let i = 0; i < k; i++) {
        const a = normWord(pw[pw.length - k + i]);
        const b = normWord(nw[s + i]);
        if (!a || !b) { ok = false; break; }
        if (a === b) continue;
        if (s === 0 && i === k - 1) {
          // полуслово-обрыв: prev-слово (≥3 букв) — префикс слова next
          if (a.length >= 3 && b.length > a.length && b.startsWith(a)) {
            trustNext = true;
            continue;
          }
          // расхождение декода на краю — допустимо только при точном якоре (k≥2)
          if (k >= 2) {
            trustNext = true;
            continue;
          }
        }
        ok = false;
        break;
      }
      if (!ok) continue;
      if (trustNext) {
        const head = pw.slice(0, pw.length - k).join(' ');
        return head ? `${head} ${decap(next)}` : next;
      }
      const tail = nw.slice(s + k).join(' ');
      return tail ? `${prev} ${tail}` : prev;
    }
  }
  return `${prev} ${next}`;
}

// [MM:SS] (или H:MM:SS для сессий длиннее часа) — моно-таймкод.
export function formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hh = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Заканчивается ли текст знаком конца предложения.
export function endsSentence(text) {
  return SENTENCE_END_RE.test((text || '').trim());
}

// Пересборка финалов [{text, start, end}] в абзацы [{startSec, text}].
// Новый абзац ⟺ (текущий абзац закончен знаком И gap ≥ PARA_GAP_SEC) ИЛИ gap ≥ HARD_GAP_SEC,
// где gap = cur.start − prev.end. Иначе финал приклеивается через пробел — так пауза-раздумье
// посреди фразы и принудительный разрез монолога не рвут предложение.
export function reflowFinals(finals = []) {
  const paras = [];
  let cur = null;      // формируемый абзац {startSec, text}
  let prevEnd = null;  // end предыдущего НЕпустого финала
  for (const f of finals) {
    const text = (f.text || '').trim();
    if (!text) continue;
    const start = Number(f.start) || 0;
    if (cur) {
      const gap = prevEnd == null ? 0 : start - prevEnd;
      const breakPara = (endsSentence(cur.text) && gap >= PARA_GAP_SEC) || gap >= HARD_GAP_SEC;
      if (breakPara) {
        paras.push(cur);
        cur = { startSec: start, text };
      } else if (gap < DEDUP_GAP_MAX_SEC) {
        // сегменты перекрываются по времени (форс-сплит) — режем дубль на стыке
        cur.text = mergeJunction(cur.text, text);
      } else {
        cur.text += ` ${text}`;
      }
    } else {
      cur = { startSec: start, text };
    }
    // Number.isFinite без коэрции: валидный end (включая 0) обновляет prevEnd,
    // отсутствующий/кривой — оставляет предыдущий (|| глотал бы легитимный 0).
    prevEnd = Number.isFinite(f.end) ? f.end : prevEnd;
  }
  if (cur) paras.push(cur);
  return paras;
}

// Абзацы → плоский текст: через пустую строку, при withTimecodes — с префиксом [MM:SS].
export function paragraphsToText(paras = [], { withTimecodes = false } = {}) {
  return paras
    .map((p) => (withTimecodes ? `[${formatMMSS(p.startSec)}] ${p.text}` : p.text))
    .join('\n\n');
}
